import { useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import Scrollbar from 'src/components/Scrollbar';
import { useSettingsTokens } from 'src/content/applications/Settings/CompanySettings/settingsUi';
import { SidebarContext } from 'src/Context/SidebarContext';
import apiFetch from 'src/Common/api/auth/Auth';
import { useCurrentPlan } from 'src/Common/hooks/useCurrentPlan';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  styled,
  Divider,
  useTheme,
  Button,
  lighten,
  darken,
  CircularProgress
} from '@mui/material';
import SidebarMenu from './SidebarMenu';
import { IEmployeeProfile } from 'src/Common/Interfaces/Profile/IEmployeeProfile';
import { Link as RouterLink } from 'react-router-dom';
import { isBillingRestricted } from 'src/Common/navigation/billingRestriction';

// Footer button of the Full Modern look (and of every company before the new look) —
// kept verbatim for Modern.
const MODERN_FOOTER_BUTTON_SX = {
  background: 'linear-gradient(135deg,#E45A0D,#C44A00)',
  '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 },
  boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)',
  border: 'none'
};

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
    width: ${theme.sidebar.width};
    min-width: ${theme.sidebar.width};
    color: ${(theme as any).sidebar.textColor};
    position: relative;
    z-index: 7;
    height: 100%;
    transition: background 0.3s ease, color 0.3s ease;
`
);

function Sidebar() {
  const { t } = useTranslation();
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState<IEmployeeProfile | null>(null);

  // Fetch employee profile from localStorage
  useEffect(() => {
    const storedEmployeeProfile = localStorage.getItem("employeeProfile");
    if (storedEmployeeProfile) {
      try {
        const parsedProfile = JSON.parse(storedEmployeeProfile) as IEmployeeProfile;
        setEmployeeProfile(parsedProfile);
      } catch (error) {
        console.error("Error parsing employee profile:", error);
      }
    }
  }, []);

  const { currentPlan: planFromHook, loading: loadingPlan } = useCurrentPlan(employeeProfile?.companyId);

  useEffect(() => {
    if (planFromHook !== undefined) setCurrentPlan(planFromHook);
    setLoading(!!loadingPlan);
  }, [planFromHook, loadingPlan]);

  const getButtonConfig = () => {
    if (!currentPlan) {
      return { 
        text: t('sidebar.upgradeToPro'), 
        href: '/subscriptions/upgrade', 
        color: 'warning' 
      };
    }

    // Use correct property names from API response
    const planName = currentPlan.packageName;
    const isTrial = currentPlan.planDetails.isTrial;
    const daysLeft = currentPlan.daysRemaining;

    if (isTrial) {
      return {
        text: t('sidebar.upgradeToProDaysLeft', { days: daysLeft }),
        href: '/subscriptions/upgrade',
        color: 'warning'
      };
    }
    
    if (planName === 'Small Business') {
      return {
        text: t('sidebar.upgradeToEnterprise'),
        href: '/subscriptions/upgrade',
        color: 'primary'
      };
    }

    if (planName === 'Professional') {
      return {
        text: t('sidebar.upgradeToEnterprise'),
        href: '/subscriptions/upgrade',
        color: 'primary'
      };
    }

    if (planName === 'Enterprise') {
      return {
        text: t('sidebar.contactSupport'),
        href: 'https://wa.me/923174088510?text=Regarding%20Enterprise%20Subscription',
        color: 'success'
      };
    }

    return { 
      text: t('sidebar.upgradeToPro'), 
      href: '/subscriptions/upgrade', 
      color: 'warning' 
    };
  };

  const closeSidebar = () => toggleSidebar();
  const billingRestricted = isBillingRestricted();
  const buttonConfig = getButtonConfig();

  // Visual-only switch: the company "Full Modern" look keeps its own design untouched; the
  // classic path gets the calm Claude / ChatGPT chrome (flat panel, hairlines, no shadows).
  const isModern = Boolean((theme as any).isModern);
  const isDark = theme.palette.mode === 'dark';
  const k = useSettingsTokens();

  // Classic: a quiet grey scroll thumb (the dark scheme's thumb is brand orange). The shared
  // Scrollbar reads theme.sidebar.scrollbarColor, so only that one value is swapped here.
  const scrollTheme = useMemo<Theme | null>(
    () =>
      isModern
        ? null
        : ({ ...theme, sidebar: { ...theme.sidebar, scrollbarColor: k.toggleOff } } as Theme),
    [isModern, theme, k]
  );
  const withScrollTheme = (node: ReactNode) =>
    scrollTheme ? <ThemeProvider theme={scrollTheme}>{node}</ThemeProvider> : node;

  // Classic footer: a flat card-like button (1px hairline, 12px corners, no gradient/shadow).
  const classicFooterSx = {
    justifyContent: 'flex-start',
    gap: '10px',
    minHeight: 40,
    px: '12px',
    py: '8px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    textAlign: 'left' as const,
    color: k.text,
    background: isDark ? k.subtleBg : k.pageBg,
    border: `1px solid ${k.border}`,
    borderRadius: '12px',
    boxShadow: 'none',
    '&:hover': { background: k.hover, boxShadow: 'none' },
    '&.Mui-focusVisible': { boxShadow: 'none', outline: `2px solid ${k.accent}`, outlineOffset: '2px' },
    '&.Mui-disabled': {
      color: k.muted,
      background: isDark ? k.subtleBg : k.pageBg,
      border: `1px solid ${k.border}`,
      opacity: 1
    },
    '& .MuiButton-startIcon': {
      m: 0,
      color: k.navText,
      '& > *:nth-of-type(1)': { fontSize: 18 }
    }
  };
  const footerSx = isModern ? MODERN_FOOTER_BUTTON_SX : classicFooterSx;
  const footerLoadingSx = isModern
    ? MODERN_FOOTER_BUTTON_SX
    : { ...classicFooterSx, justifyContent: 'center' };
  const footerIcon = (kind: 'billing' | 'plan') => {
    if (isModern) return undefined;
    if (kind === 'billing') return <CreditCardOutlinedIcon />;
    return buttonConfig.href.startsWith('http') ? <SupportAgentOutlinedIcon /> : <AutoAwesomeOutlinedIcon />;
  };

  // Logo strip: as tall as the header so its hairline meets the header's bottom edge.
  // Modern: no surface of its own — the page gradient runs behind the logo strip so the
  // sidebar reads as part of one continuous canvas. Classic: the flat sidebar panel colour.
  const logoStripSx = {
    height: theme.header.height,
    display: 'flex',
    alignItems: 'center',
    px: isModern ? 2 : '14px',
    bgcolor: 'transparent',
    borderBottom: `1px solid ${(theme as any).sidebar.dividerBg}`,
    flexShrink: 0,
    transition: 'background-color 0.3s ease'
  };
  const logo = (
    <Box
      component="img"
      // The sidebar canvas is dark in every modern variant, so use the white logo
      // asset rather than CSS-inverting the dark one.
      src={(isModern && !(theme as any).modernPaper) || isDark
        ? '/sum_cloud_pos_full_logo_white.png'
        : '/sum_cloud_pos_full_logo.png'}
      alt="Meta Sum Cloud POS"
      sx={{ height: isModern ? 64 : 48, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
    />
  );

  return (
    <>
      <SidebarWrapper
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: (theme) => (theme as any).sidebar.background,
          border: 'none',
          boxShadow: 'none',
          ...(isModern ? {} : { borderRight: `1px solid ${k.border}` }),
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        {/* Fixed Logo Area */}
        <Box sx={logoStripSx}>{logo}</Box>

        {/* Scrollable Menu Area */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {withScrollTheme(
            <Scrollbar>
              {isModern && (
                <Divider
                  sx={{
                    mx: theme.spacing(2),
                    background: (theme) => (theme as any).sidebar.dividerBg
                  }}
                />
              )}
              <SidebarMenu />
            </Scrollbar>
          )}
        </Box>

        {/* Fixed Bottom Button Area */}
        <Box sx={{ flexShrink: 0, mt: 'auto' }}>
          <Divider sx={{ background: (theme) => (theme as any).sidebar.dividerBg }} />
          <Box p={isModern ? 2 : '12px'}>
            {loading ? (
              <Button
                fullWidth
                variant="contained"
                sx={footerLoadingSx}
                disabled
              >
                <CircularProgress size={isModern ? 24 : 18} color="inherit" />
              </Button>
            ) : billingRestricted ? (
              <Button
                component={RouterLink}
                to="/management/billing"
                onClick={closeSidebar}
                variant="contained"
                sx={footerSx}
                size="small"
                fullWidth
                startIcon={footerIcon('billing')}
              >
                {t('menu.billing')}
              </Button>
            ) : (
              <Button
                href={buttonConfig.href}
                target={buttonConfig.text === 'Contact Support' ? '_blank' : '_self'}
                rel="noopener noreferrer"
                variant="contained"
                sx={footerSx}
                size="small"
                fullWidth
                startIcon={footerIcon('plan')}
              >
                {buttonConfig.text}
              </Button>
            )}
          </Box>
        </Box>
      </SidebarWrapper>

      <Drawer
        sx={{ boxShadow: `${theme.sidebar.boxShadow}` }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
        PaperProps={
          isModern
            ? undefined
            : {
                sx: {
                  boxShadow: 'none',
                  backgroundImage: 'none',
                  bgcolor: (theme as any).sidebar.background,
                  borderRight: theme.direction === 'rtl' ? 0 : `1px solid ${k.border}`,
                  borderLeft: theme.direction === 'rtl' ? `1px solid ${k.border}` : 0
                }
              }
        }
      >
        <SidebarWrapper
          sx={{
            background: (theme) => (theme as any).sidebar.background,
            border: 'none',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh'
          }}
        >
          {/* Fixed Logo Area */}
          <Box sx={logoStripSx}>{logo}</Box>

          {/* Scrollable Menu Area */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {withScrollTheme(
              <Scrollbar>
                {isModern && (
                  <Divider
                    sx={{
                      mx: theme.spacing(2),
                      background: (theme) => (theme as any).sidebar.dividerBg
                    }}
                  />
                )}
                <SidebarMenu />
              </Scrollbar>
            )}
          </Box>

          {/* Fixed Bottom Button Area */}
          <Box sx={{ flexShrink: 0, mt: 'auto' }}>
            <Divider sx={{ background: (theme) => (theme as any).sidebar.dividerBg }} />
            <Box p={isModern ? 2 : '12px'}>
              {loading ? (
                <Button
                  fullWidth
                  variant="contained"
                  sx={footerLoadingSx}
                  disabled
                >
                  <CircularProgress size={isModern ? 24 : 18} color="inherit" />
                </Button>
              ) : billingRestricted ? (
                <Button
                  component={RouterLink}
                  to="/management/billing"
                  onClick={closeSidebar}
                  variant="contained"
                  sx={footerSx}
                  size="small"
                  fullWidth
                  startIcon={footerIcon('billing')}
                >
                  {t('menu.billing')}
                </Button>
              ) : (
                <Button
                  href={buttonConfig.href}
                  target={buttonConfig.text === t('sidebar.contactSupport') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={footerSx}
                  size="small"
                  fullWidth
                  startIcon={footerIcon('plan')}
                >
                  {buttonConfig.text}
                </Button>
              )}
            </Box>
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;